import { MeetingPrepDeliveryTiming } from '@ViewModels';
import moment from 'moment';

/**
 * Helpers for the Meeting Prep send-time picker. The API stores `sendHourLocal` as a whole hour
 * (0–23) in the user's local timezone, so the picker only offers on-the-hour options.
 */

export const DEFAULT_SEND_HOUR = 7;
export const DEFAULT_EVENING_SEND_HOUR = 19;

/**
 * Evening-mode window, inclusive. The API rejects a PUT whose hour falls outside this range while
 * `deliveryTiming` is `EveningBefore`, so these must stay in step with `MeetingPrepDelivery` server-side.
 */
export const EVENING_SEND_HOUR_MIN = 16;
export const EVENING_SEND_HOUR_MAX = 22;

export interface SendHourOption {
	value: number;
	label: string;
}

/** Format a 0–23 hour as a 12-hour clock label, e.g. 0 → "12:00 AM", 7 → "7:00 AM", 13 → "1:00 PM". */
export function formatHourLabel(hour: number): string {
	const normalized = ((hour % 24) + 24) % 24;
	const period = normalized < 12 ? 'AM' : 'PM';
	const hour12 = normalized % 12 === 0 ? 12 : normalized % 12;
	return `${hour12}:00 ${period}`;
}

/** All 24 whole-hour options in morning-of mode; only the evening window in evening-before mode. */
export function getSendHourOptions(
	timing: MeetingPrepDeliveryTiming = MeetingPrepDeliveryTiming.MorningOf
): SendHourOption[] {
	const [first, last] =
		timing === MeetingPrepDeliveryTiming.EveningBefore ? [EVENING_SEND_HOUR_MIN, EVENING_SEND_HOUR_MAX] : [0, 23];

	return Array.from({ length: last - first + 1 }, (_, index) => {
		const hour = first + index;
		return { value: hour, label: formatHourLabel(hour) };
	});
}

export function getDefaultSendHour(timing: MeetingPrepDeliveryTiming): number {
	return timing === MeetingPrepDeliveryTiming.EveningBefore ? DEFAULT_EVENING_SEND_HOUR : DEFAULT_SEND_HOUR;
}

/** Keeps a mode switch from leaving the form holding an hour the API would reject. */
export function coerceSendHourForTiming(timing: MeetingPrepDeliveryTiming, sendHourLocal: number): number {
	if (timing !== MeetingPrepDeliveryTiming.EveningBefore) {
		return sendHourLocal;
	}

	return sendHourLocal >= EVENING_SEND_HOUR_MIN && sendHourLocal <= EVENING_SEND_HOUR_MAX
		? sendHourLocal
		: DEFAULT_EVENING_SEND_HOUR;
}

/**
 * "Last delivered" line. In evening mode the send and the day it covers are different days, so the
 * coverage day is spelled out rather than left for the reader to infer.
 */
export function formatLastDeliveredLabel(lastRunDate: string, timing: MeetingPrepDeliveryTiming): string {
	const sent = moment(lastRunDate);
	const sentLabel = sent.format('MMM D [at] h:mm A');

	return timing === MeetingPrepDeliveryTiming.EveningBefore
		? `Last delivered: ${sentLabel} (covering ${sent.clone().add(1, 'day').format('MMM D')})`
		: `Last delivered: ${sentLabel}`;
}
